/**
 * App-wide configuration.
 *
 * Sources values from Vite's `import.meta.env` so different environments
 * (dev / staging / prod) can point at different Supabase projects without
 * code changes.
 *
 * All three values below are PUBLIC by design:
 *   - SUPABASE_URL is just the project's REST endpoint
 *   - SUPABASE_ANON_KEY is a JWT minted with the `anon` role; it has no
 *     intrinsic permissions beyond what RLS allows for unauthenticated
 *     requests, which is effectively zero data access
 *   - ALLOWED_EMAIL_DOMAIN is a whitelist string
 *
 * Never put a service-role key here. Never put a database password here.
 * See SECURITY.md § "What's NOT public".
 */

function required(name: string, value: string | undefined): string {
  if (!value || value.length === 0) {
    throw new Error(
      `Missing env var ${name}. Copy .env.example to .env and fill it in.`,
    );
  }
  return value;
}

export const SUPABASE_URL = required(
  "VITE_SUPABASE_URL",
  import.meta.env.VITE_SUPABASE_URL,
);

export const SUPABASE_ANON_KEY = required(
  "VITE_SUPABASE_ANON_KEY",
  import.meta.env.VITE_SUPABASE_ANON_KEY,
);

export const ALLOWED_EMAIL_DOMAIN =
  import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN ?? "icons.it";

/** True when the page is served from a localhost address. Used to gate the
 *  demo button + a couple of dev affordances; never exposes real data. */
export const IS_LOCALHOST = (() => {
  if (typeof location === "undefined") return false;
  const h = location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "0.0.0.0" ||
    h.endsWith(".local")
  );
})();

/** localStorage key for the offline state cache. Bump the version suffix
 *  on schema changes to invalidate stale caches. */
export const LS_STATE_KEY = "icons_presence_v4";

/** sessionStorage key for the "set password" prompt dismissal. Scoped to
 *  the browser SESSION (not the device): a user who taps "Skip for now" is
 *  re-prompted on their next sign-in, and keeps being re-prompted until
 *  they actually set a password. It used to be a localStorage flag, which
 *  suppressed the prompt permanently — leaving magic-link users unable to
 *  ever reach the set-password modal again. */
export const PW_DISMISS_KEY = "icons_pw_setup_dismissed_v1";

/** Year range allowed in the year picker. */
export const YEAR_RANGE = [2024, 2025, 2026, 2027, 2028] as const;

/** Months — kept in code (not i18n) because the UI is English-only. */
export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
